import { useEffect, useState } from 'react';
import List from 'anchor-ui/list';
import { IconClose } from 'anchor-ui/icons';
import { getActiveColor, getSecondaryColor } from '../../utils/activeColor';
import Avatar from 'anchor-ui/avatar';
import { ActiveChannelInterface } from '../../interfaces/activeChannelInterface';
import { getFontStyle } from '../../utils/getFontStyle';
import { getAvatar } from '../../utils/getAvatar';
import { getProfileFromMetadata } from '../../utils/updateMetadata';
import { UserSettingsInterface } from '../../interfaces/userSettingsInterface';
import ChannelListItem from './ChannelListItem';

const ChannelListComponent = ({
  members,
  channels,
  activeChannels,
  onlineMembersCount,
  unreadCount,
  handleChangeChannel,
  currentChat,
  handleLeaveChannel,
  generalFontSize,
  userSettings,
  loggedInUser,
}: {
  members: CometChat.GroupMember[];
  channels: CometChat.Group[];
  activeChannels: ActiveChannelInterface[];
  onlineMembersCount: { [key: string]: number } | null;
  unreadCount: { [id: string]: { nr: number; chat: ActiveChannelInterface } };
  handleChangeChannel: (channel: ActiveChannelInterface) => void;
  currentChat: ActiveChannelInterface;
  handleLeaveChannel: (channel: ActiveChannelInterface) => void;
  generalFontSize: string;
  userSettings: UserSettingsInterface;
  loggedInUser: CometChat.User | null;
}) => {
  const userProfile = getProfileFromMetadata(loggedInUser!);
  const favoriteChannelNames = userProfile?.favoriteChannels || [];

  const kingsdayChannel = channels.find((channel) => channel.getName() === 'Koningsdag');
  const activeChannelsWithoutCurrent = activeChannels.filter((channel) => channel.id !== currentChat.id);
  const onlineMembers = members.filter((member) => member.getStatus() === 'online');

  const [currentChatIsPublic, setCurrentChatIsPublic] = useState(false);

  const isKingsdayActive =
    currentChat.name === 'Koningsdag' || activeChannels.some((channel) => channel.name === 'Koningsdag');

  const LeaveChannelButton = ({ channel }: { channel: ActiveChannelInterface }) => {
    return (
      <button
        type="button"
        className="li-button text-white"
        onClick={(event) => {
          event.stopPropagation();
          handleLeaveChannel(channel);
        }}
      >
        <IconClose color={'white'} />
      </button>
    );
  };

  const getOnlineMembersCount = (channel: { guid: string; onlineMembersCount?: number }) => {
    let count = onlineMembersCount?.[channel.guid] ?? channel.onlineMembersCount ?? 0;
    if (count === 404) count = 1;
    return `${count} online`;
  };

  useEffect(() => {
    const isActiveAndPublic = activeChannels.some((channel) => channel.id === currentChat.id);
    setCurrentChatIsPublic(isActiveAndPublic);
  }, [currentChat, activeChannels]);

  const allChannels = [...channels].sort((a, b) => {
    const aIsFavorite = favoriteChannelNames.includes(a.getName());
    const bIsFavorite = favoriteChannelNames.includes(b.getName());

    if (aIsFavorite && !bIsFavorite) return -1;
    if (!aIsFavorite && bIsFavorite) return 1;

    const countA = (a as any).onlineMembersCount ?? 0;
    const countB = (b as any).onlineMembersCount ?? 0;
    return countB - countA;
  });

  const publicChannels = allChannels.filter(
    (channel) =>
      channel.getGuid() !== currentChat.id &&
      !activeChannels.some((activeChannel) => activeChannel.id === channel.getGuid()) &&
      channel.getName() !== 'Koningsdag'
  );

  const shouldShowKingsday = !!kingsdayChannel && !isKingsdayActive;
  const kingsdayChannelData = shouldShowKingsday
    ? {
      id: kingsdayChannel!.getGuid(),
      name: kingsdayChannel!.getName(),
      icon: kingsdayChannel!.getIcon(),
      onlineMembersCount: (kingsdayChannel as any)?.onlineMembersCount ?? 404,
      joinedAt: 0,
      isGroup: true,
    }
    : null;

  return (
    <div>
      <div className="active-channels">
        {currentChatIsPublic && (
          <List
            header="Huidig Kanaal"
            headerStyle={getFontStyle(generalFontSize)}
            style={{ backgroundColor: getSecondaryColor() }}
          >
            <ChannelListItem
              primaryText={currentChat.name}
              primaryTextStyle={getFontStyle(generalFontSize)}
              secondaryText={
                onlineMembers.length
                  ? onlineMembers.length + ' online'
                  : getOnlineMembersCount({
                    guid: currentChat.id,
                    onlineMembersCount: currentChat.onlineMembersCount,
                  })
              }
              avatar={
                <Avatar
                  image={getAvatar(currentChat.name, true)}
                  style={{ backgroundColor: getActiveColor() }}
                />
              }
              rightButton={<LeaveChannelButton channel={currentChat} />}
              activeChannel={true}
              className="channel-item"
            />
          </List>
        )}

        {activeChannelsWithoutCurrent.length > 0 && (
          <List
            header="Actieve Kanalen"
            headerStyle={getFontStyle(generalFontSize)}
            style={{ backgroundColor: getSecondaryColor() }}
          >
            {activeChannelsWithoutCurrent.map((channel, index) => {
              const channelUnreadCount = unreadCount[channel.id] ? unreadCount[channel.id].nr : 0;

              return (
                <ChannelListItem
                  key={index}
                  secondaryText={getOnlineMembersCount({
                    guid: channel.id,
                    onlineMembersCount: (channel as any).onlineMembersCount,
                  })}
                  primaryText={channel.name}
                  primaryTextStyle={getFontStyle(generalFontSize)}
                  badge={
                    channelUnreadCount > 0 && <div className="badge-unread">{channelUnreadCount}</div>
                  }
                  avatar={
                    <Avatar
                      image={getAvatar(channel.name, true)}
                      style={{ backgroundColor: getActiveColor() }}
                    />
                  }
                  onClick={() => handleChangeChannel(channel)}
                  className={`channel-item active`}
                  rightButton={<LeaveChannelButton channel={channel} />}
                  activeChannel={true}
                />
              );
            })}
          </List>
        )}
      </div>

      {shouldShowKingsday && (
        <List
          header="Koningsdag 👑"
          headerStyle={getFontStyle(generalFontSize)}
          style={{ backgroundColor: getSecondaryColor() }}
        >
          <ChannelListItem
            primaryText={kingsdayChannel!.getName()}
            primaryTextStyle={getFontStyle(generalFontSize)}
            secondaryText={getOnlineMembersCount({
              guid: kingsdayChannel!.getGuid(),
              onlineMembersCount: (kingsdayChannel as any)?.onlineMembersCount,
            })}
            avatar={
              <Avatar
                image={getAvatar(kingsdayChannel!.getName())}
                style={{ backgroundColor: getActiveColor() }}
              />
            }
            onClick={() => handleChangeChannel(kingsdayChannelData!)}
            className="channel-item"
          />
        </List>
      )}

      <List
        header="Publieke Kanalen"
        headerStyle={getFontStyle(generalFontSize)}
        style={{ backgroundColor: getSecondaryColor() }}
      >
        {publicChannels.map((channel, index) => {
          return (
            <div key={index}>
              <ChannelListItem
                primaryText={channel.getName()}
                primaryTextStyle={getFontStyle(generalFontSize)}
                secondaryText={getOnlineMembersCount({
                  guid: channel.getGuid(),
                  onlineMembersCount: (channel as any).onlineMembersCount,
                })}
                avatar={
                  <Avatar
                    image={getAvatar(channel.getName())}
                    style={{ backgroundColor: getActiveColor() }}
                  />
                }
                onClick={() =>
                  handleChangeChannel({
                    id: channel.getGuid(),
                    name: channel.getName(),
                    icon: channel.getIcon(),
                    onlineMembersCount: (channel as any).onlineMembersCount ?? 404,
                    joinedAt: 0,
                    isGroup: true,
                  })
                }
                className="channel-item"
              />
            </div>
          );
        })}
      </List>
    </div>
  );
};

export default ChannelListComponent;
